// The picture of three axes on a slant with a box drawn from O. The faces of
// the box are see-through, so the marks behind them still show; the parts
// of the axes behind the box are dashed. Shaded like the cube stacks: the
// top lightest, the right side darkest. Plain React SVG, like
// practice-graphs.tsx.

import {
  AXES_TEXT_SIZE,
  axes3dPicture,
  describeAxes3d,
  type AxesPoint,
  type Axes3dSpec,
} from './practice-axes-3d-data';

const INK = '#2d4a3e';
const SHADE = { top: '#eef3ee', left: '#cfe0d4', right: '#a9c4b2' };

const fixed = (value: number) => value.toFixed(2);
const path = (points: AxesPoint[]) =>
  `${points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${fixed(point.x)} ${fixed(point.y)}`).join(' ')} Z`;

export function Axes3dView({ axes }: { axes: Axes3dSpec }) {
  const picture = axes3dPicture(axes);
  const { box } = picture;

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeAxes3d(axes)}
        viewBox={`${fixed(box.x)} ${fixed(box.y)} ${fixed(box.width)} ${fixed(box.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(box.width * 1.4))}px` }}
      >
        {picture.faces.map((face) => (
          <path
            key={face.side}
            data-face={face.side}
            d={path(face.points)}
            fill={SHADE[face.side]}
            fillOpacity="0.55"
            stroke="none"
          />
        ))}
        {picture.axes.map((axis) => (
          <g key={axis.name} data-axis={axis.name}>
            <line
              data-part="hidden"
              x1={fixed(axis.hidden.from.x)}
              y1={fixed(axis.hidden.from.y)}
              x2={fixed(axis.hidden.to.x)}
              y2={fixed(axis.hidden.to.y)}
              stroke={INK}
              strokeWidth="1.6"
              strokeDasharray="5 4"
            />
            <line
              x1={fixed(axis.shown.from.x)}
              y1={fixed(axis.shown.from.y)}
              x2={fixed(axis.shown.to.x)}
              y2={fixed(axis.shown.to.y)}
              stroke={INK}
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </g>
        ))}
        {picture.ticks.map((tick, index) => (
          <line
            key={index}
            data-part="tick"
            x1={fixed(tick.from.x)}
            y1={fixed(tick.from.y)}
            x2={fixed(tick.to.x)}
            y2={fixed(tick.to.y)}
            stroke={INK}
            strokeWidth="1.4"
          />
        ))}
        {picture.edges.map((edge, index) => (
          <line
            key={index}
            data-part="edge"
            x1={fixed(edge.from.x)}
            y1={fixed(edge.from.y)}
            x2={fixed(edge.to.x)}
            y2={fixed(edge.to.y)}
            stroke={INK}
            strokeWidth="2"
            strokeLinecap="round"
          />
        ))}
        {picture.dots.map((dot, index) => (
          <circle
            key={index}
            data-part="corner"
            cx={fixed(dot.x)}
            cy={fixed(dot.y)}
            r="3"
            fill={INK}
          />
        ))}
        {picture.texts.map((text, index) => (
          <text
            key={index}
            data-label={text.part}
            x={fixed(text.x)}
            y={fixed(text.y)}
            dy="0.35em"
            textAnchor="middle"
            fill={INK}
            fontSize={
              text.part === 'tick' ? AXES_TEXT_SIZE - 2 : AXES_TEXT_SIZE
            }
            fontWeight={text.part === 'tick' ? '600' : '700'}
            fontStyle={text.part === 'axis' ? 'italic' : undefined}
            stroke="#ffffff"
            strokeWidth="3"
            paintOrder="stroke"
          >
            {text.text}
          </text>
        ))}
      </svg>
    </figure>
  );
}
